import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { mapValidationWarnings } from '@/services/core/validationWarnings'
import { Election } from '@/types/models'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`

const JSON_HEADERS = { 'Content-Type': 'application/json' }

interface ApiElection {
  id: string
  name: string
  description?: string
  infoUrl?: string
  electionDate?: string
  type?: string
  location?: string
  testOnly?: boolean
  validationWarnings?: unknown
}

function toLocal(raw: ApiElection): Election {
  return {
    id: raw.id,
    name: raw.name ?? '',
    description: raw.description ?? '',
    infoUrl: raw.infoUrl ?? '',
    electionDate: raw.electionDate ?? '',
    type: raw.type ?? '',
    location: raw.location ?? '',
    testOnly: raw.testOnly ?? false,
    validationWarnings: mapValidationWarnings(raw.validationWarnings),
  }
}

export const ElectionsApi = {
  getAll: async (): Promise<Election[]> => {
    const res = await apiFetch(`${API_BASE}/elections?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) throw new Error(`Failed to fetch elections (${res.status})`)
    const data = await res.json()
    const results = toArray<ApiElection>(data)
    return results.map(toLocal)
  },

  getById: async (id: string): Promise<Election | null> => {
    const res = await apiFetch(`${API_BASE}/elections/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (res.status === 404) return null
    if (!res.ok) throw new Error(`Failed to fetch election (${res.status})`)
    return toLocal(await res.json())
  },

  upsert: async (election: Election): Promise<Election> => {
    const res = await apiFetch(`${API_BASE}/elections/${election.id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        id: election.id,
        name: election.name,
        description: election.description,
        infoUrl: election.infoUrl,
        electionDate: election.electionDate,
        type: election.type,
        location: election.location,
        testOnly: election.testOnly,
      }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save election')
    return toLocal(await res.json())
  },
}
